import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { CURRENT_SEASON } from '../../core/current-season.js';
import { adminLeagueDataSurface, effectiveAdminLeagueId, getActiveLeagueId, withLeagueId } from '../league-context.js';

const FIXTURE_QUICK_RANGES = [
  { key: 'thisWeek', label: 'This week' },
  { key: 'lastWeek', label: 'Last week' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'nextMonth', label: 'Next month' },
  { key: 'all', label: 'All fixtures' },
];

const FIXTURE_RANGE_URL_PARAM = 'range';
const DEFAULT_FIXTURE_QUICK = 'thisMonth';
const FIXTURE_QUICK_KEYS = new Set(FIXTURE_QUICK_RANGES.map((o) => o.key));

function normalizeFixtureQuickKey(key) {
  const k = String(key || '').trim();
  return FIXTURE_QUICK_KEYS.has(k) ? k : DEFAULT_FIXTURE_QUICK;
}

function readFixtureQuickFromUrl() {
  try {
    const url = new URL(globalThis.location.href);
    const raw = url.searchParams.get(FIXTURE_RANGE_URL_PARAM);
    if (raw == null || raw === '') return DEFAULT_FIXTURE_QUICK;
    return normalizeFixtureQuickKey(raw);
  } catch {
    return DEFAULT_FIXTURE_QUICK;
  }
}

Alpine.data('fixturesList', () => ({

  // UI
  view: 'calendar',
  q: '',
  quick: DEFAULT_FIXTURE_QUICK,

  // Data
  allItems: [],             // raw fixtures from API (ALL for the season)
  allWeeks: [],             // weeks built from filtered items
  mapLinks: {},
  loading: true,
  refreshSpinning: false,
  firstLoad: true,
  error: null,

  _leagueSnap: '',
  leagueName: '',

  // Query params for /fixtures API (not Pinecone route params)
  fetchParams: {
    season: CURRENT_SEASON,
    league: getActiveLeagueId('PDL'),
    sort: 'startAt:asc',
    populate: 'true',
    page: '1',
    limit: '2000',          // plenty for whole season
  },

  // ---------- Derived ----------
  get filteredWeeks() {
    // Recompute weeks whenever filters change
    const filtered = this.applyFilters(this.allItems);
    this.buildMapLinks(filtered);
    return this.buildWeeks(filtered);
  },

  get quickLabel() {
    return FIXTURE_QUICK_RANGES.find((o) => o.key === this.quick)?.label || 'Custom';
  },

  get hasAnyFixtures() {
    return this.allItems.length > 0;
  },

  /** Fixtures exist for the season but the current range/search hides them all. */
  get isFilterEmpty() {
    return !this.filteredWeeks.length && this.hasAnyFixtures;
  },

  /** No fixtures loaded for this league/season at all. */
  get isSeasonEmpty() {
    return !this.loading && !this.hasAnyFixtures;
  },

  get calendarCards() {
    const cards = [];
    for (const w of this.filteredWeeks) {
      if (w.type === 'break' || w.type === 'event') {
        cards.push({ cardType: w.type, week: w });
        continue;
      }
      for (const m of w.matches || []) {
        cards.push({ cardType: 'match', week: w, match: m });
      }
    }
    return cards;
  },

  quickOptions() {
    return FIXTURE_QUICK_RANGES;
  },

  // ---------- Init ----------
  leagueDisplayLabel() {
    const name = String(this.leagueName || '').trim();
    return name || this.fetchParams.league || 'PDL';
  },

  async loadLeagueDisplayName() {
    const leagueKey = this.fetchParams.league;
    const seasonQs = this.fetchParams.season
      ? `?season=${encodeURIComponent(this.fetchParams.season)}`
      : '';
    try {
      const res = await apiFetch(`/leagues/${encodeURIComponent(leagueKey)}${seasonQs}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.league?.name) {
        this.leagueName = String(data.league.name).trim();
      } else {
        this.leagueName = '';
      }
    } catch {
      this.leagueName = '';
    }
  },

  syncQuickToUrl() {
    try {
      const url = new URL(globalThis.location.href);
      url.searchParams.set(FIXTURE_RANGE_URL_PARAM, this.quick);
      const next = `${url.pathname}${url.search}${url.hash}`;
      const now = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
      if (now !== next) {
        globalThis.history.replaceState({}, '', next);
      }
    } catch {
      /* ignore */
    }
  },

  applyQuickFromUrl() {
    this.quick = readFixtureQuickFromUrl();
  },

  async init() {
    this.applyQuickFromUrl();
    this.syncQuickToUrl();
    this.fetchParams.league = effectiveAdminLeagueId('PDL');
    this._leagueSnap = this.fetchParams.league;
    await Promise.all([this.loadLeagueDisplayName(), this.fetchAll()]);
    this.firstLoad = false;

    this._onPopState = () => {
      const next = readFixtureQuickFromUrl();
      if (next !== this.quick) this.quick = next;
    };
    globalThis.addEventListener('popstate', this._onPopState);

    this._onLeagueOrNav = () => {
      if (adminLeagueDataSurface() !== 'fixtures') return;
      const next = effectiveAdminLeagueId('PDL');
      if (next === this._leagueSnap) return;
      this._leagueSnap = next;
      this.fetchParams.league = next;
      void Promise.all([this.loadLeagueDisplayName(), this.fetchAll()]);
    };
    document.addEventListener('pinecone:end', this._onLeagueOrNav);
    document.addEventListener('dm:admin-league-changed', this._onLeagueOrNav);
    this.$root?.addEventListener?.('alpine:destroy', () => {
      if (this._onPopState) {
        globalThis.removeEventListener('popstate', this._onPopState);
        this._onPopState = null;
      }
      if (this._onLeagueOrNav) {
        document.removeEventListener('pinecone:end', this._onLeagueOrNav);
        document.removeEventListener('dm:admin-league-changed', this._onLeagueOrNav);
        this._onLeagueOrNav = null;
      }
    });
  },

  // ---------- Fetch everything once ----------
  async fetchAll() {
    this.loading = true; this.error = null;
    const usp = new URLSearchParams(this.fetchParams);
    try {
      const res = await apiFetch(`/fixtures?${usp.toString()}`);
      const data = await res.json();
      this.allItems = Array.isArray(data.items) ? data.items : [];
    } catch (e) {
      console.error('load fixtures failed', e);
      this.error = 'Failed to load fixtures';
      this.allItems = [];
    } finally {
      this.loading = false;
      this.refreshSpinning = false;
    }
  },

  async manualRefresh() {
    this.refreshSpinning = true;
    await this.fetchAll();
  },

  // ---------- Filters (client-side) ----------
  setQuick(key) {
    this.quick = normalizeFixtureQuickKey(key);
    this.syncQuickToUrl();
  },

  setQuickFromRangeMenu(key) {
    this.setQuick(key);
    try {
      const el = this.$refs?.fixturesRangeMenu;
      if (el && 'open' in el) el.open = false;
    } catch {
      /* ignore */
    }
  },

  clearFilters() {
    this.q = '';
    this.setQuick(DEFAULT_FIXTURE_QUICK);
    try {
      const el = this.$refs?.fixturesRangeMenu;
      if (el && 'open' in el) el.open = false;
    } catch {
      /* ignore */
    }
  },

  currentBkkYm() {
    return this.bkkDay(new Date()).slice(0, 7);
  },

  nextBkkYm() {
    const ym = this.currentBkkYm();
    const [y, m] = ym.split('-').map(Number);
    if (m === 12) return `${y + 1}-01`;
    return `${y}-${String(m + 1).padStart(2, '0')}`;
  },

  // Centralized filter logic
  applyFilters(items) {
    const q = this.q.trim().toLowerCase();

    let from = null;
    let to = null;
    let quickMonthYm = null;

    const now = new Date();

    if (this.quick === 'thisWeek') {
      const bounds = this.bkkWeekBounds(now);
      from = bounds.from;
      to = bounds.to;
    } else if (this.quick === 'lastWeek') {
      const thisWeekStart = this.startOfWeekBkk(now, 'Asia/Bangkok');
      const lastWeekAnchor = new Date(thisWeekStart.getTime() - 7 * 86400000);
      const bounds = this.bkkWeekBounds(lastWeekAnchor);
      from = bounds.from;
      to = bounds.to;
    } else if (this.quick === 'thisMonth') {
      quickMonthYm = this.currentBkkYm();
    } else if (this.quick === 'nextMonth') {
      quickMonthYm = this.nextBkkYm();
    }

    return items.filter(fx => {
      if (quickMonthYm) {
        const ym = this.bkkDay(fx.startAt).slice(0, 7);
        if (ym !== quickMonthYm) return false;
      } else if (from && to) {
        const t = new Date(fx.startAt);
        if (t < from) return false;
        if (t > to) return false;
      }

      // Search filter
      if (!q) return true;
      const t = (fx.roundLabel || fx.title || '').toLowerCase();
      const v = (fx.venueName || fx.venue?.name || fx.venueId?.name || '').toLowerCase();
      const h = (fx.homeTeamName || fx.homeTeamId?.name || '').toLowerCase();
      const a = (fx.awayTeamName || fx.awayTeamId?.name || '').toLowerCase();
      return (t.includes(q) || v.includes(q) || h.includes(q) || a.includes(q));
    });
  },

  fixtureVenueName(fx) {
    return String(fx?.venueName || fx?.venue?.name || fx?.venueId?.name || '').trim();
  },

  fixtureVenueLabel(fx) {
    return this.fixtureVenueName(fx) || 'TBC';
  },

  // ---------- Builders ----------
  buildMapLinks(items) {
    const teamSet = new Set();
    for (const fx of items) {
      const h = fx.homeTeamName || fx.homeTeamId?.name;
      const a = fx.awayTeamName || fx.awayTeamId?.name;
      if (h) teamSet.add(h);
      if (a) teamSet.add(a);
    }
    this.mapLinks = Object.fromEntries(
      [...teamSet].map(t => [t, `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t + ' Phuket')}`])
    );
  },

  buildWeeks(items) {
    // Group by Bangkok calendar day
    const byDay = new Map();
    for (const fx of items) {
      const key = this.bkkDay(fx.startAt); // YYYY-MM-DD in BKK
      (byDay.get(key) || byDay.set(key, []).get(key)).push(fx);
    }

    const weeks = [];
    for (const [date, arr] of byDay.entries()) {
      const onlyEvents = arr.every(fx => fx.type === 'event');
      const onlyBreaks = arr.every(fx => fx.type === 'break');
      const type = onlyEvents ? 'event' : onlyBreaks ? 'break' : 'regular';

      // pick a representative fixture for event/break (so we have the id)
      const rep = onlyEvents
        ? arr.find(fx => fx.type === 'event')
        : onlyBreaks
          ? arr.find(fx => fx.type === 'break')
          : null;

      // Event fields
      const evt = arr.find(fx => fx.type === 'event');
      const eventTitle = evt?.roundLabel || evt?.title || 'League Event';
      const eventVenue =
        evt?.venue || null;
      weeks.push({
        date,
        type,

        // For event/break weeks we expose the single fixture id and links
        fixtureId: rep?._id || null,
        href: rep?._id ? withLeagueId(`/admin/fixtures/${rep._id}`, this.fetchParams.league) : null,

        title: type === 'event' ? eventTitle : null,
        venue: type === 'event' ? eventVenue : null,

        // Normal matches list (for regular weeks)
        matches: arr
          .filter(fx => fx.type === 'fixture')
          .map(fx => {
            const homeName = fx.homeTeamName || fx.homeTeamId?.name || 'Home';
            const awayName = fx.awayTeamName || fx.awayTeamId?.name || 'Away';

            const homeLogo =
              fx.homeTeamLogo ||
              fx.homeTeamId?.logoUrl ||
              '/assets/placeholder-player.png';

            const awayLogo =
              fx.awayTeamLogo ||
              fx.awayTeamId?.logoUrl ||
              '/assets/placeholder-player.png';

            const legsHome = typeof fx.legsHome === 'number' ? fx.legsHome : null;
            const legsAway = typeof fx.legsAway === 'number' ? fx.legsAway : null;
            const hasResult =
              fx.status === 'completed' &&
              legsHome !== null &&
              legsAway !== null;

            return {
              id: fx._id,
              home: homeName,
              away: awayName,
              href: withLeagueId(`/admin/fixtures/${fx._id}`, this.fetchParams.league),
              homeLogo,
              awayLogo,
              venueLabel: this.fixtureVenueLabel(fx),

              status: fx.status,
              legsHome,
              legsAway,
              forfeit: !!fx.forfeit,
              hasResult,
            };
          }),
      });
    }

    weeks.sort((a, b) => a.date.localeCompare(b.date));
    this.allWeeks = weeks;
    return weeks;
  },

  // ---------- Time helpers (Bangkok-aware) ----------
  bkkDay(iso) {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' });
    return fmt.format(d); // YYYY-MM-DD
  },

  /** True when this list row is today or a future Bangkok calendar day (highlights upcoming nights). */
  isUpcomingListDay(dateYmd) {
    if (!dateYmd) return false;
    return String(dateYmd) >= this.bkkDay(new Date());
  },

  bkkWeekBounds(anchor) {
    // Compute Monday 00:00 to Sunday 23:59:59 BKK, return as real Date objects in local timezone
    const tz = 'Asia/Bangkok';
    const start = this.startOfWeekBkk(anchor, tz);              // Monday 00:00 BKK
    const end = new Date(start.getTime() + 6 * 86400000);     // Sunday 00:00 BKK
    end.setHours(23, 59, 59, 999);
    return { from: start, to: end };
  },

  startOfWeekBkk(anchor, tz) {
    const d = new Date(anchor);
    // Get Y/M/D in BKK
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' })
      .formatToParts(d)
      .reduce((acc, p) => (acc[p.type] = p.value, acc), {});
    const y = +parts.year, m = +parts.month, day = +parts.day;
    // Recreate BKK local date at 00:00
    const bkkMidnight = new Date(`${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00+07:00`);
    // Determine weekday (0=Sun..6=Sat) in BKK by reading back pieces
    const wk = new Date(bkkMidnight).getDay();
    const delta = (wk === 0 ? -6 : 1 - wk);
    const mon = new Date(bkkMidnight.getTime() + delta * 86400000);
    return mon; // BKK wall time Date
  },

  fixturesBuilderHref() {
    return withLeagueId('/admin/fixtures-builder', this.fetchParams.league);
  },

  canEditFixtures() {
    return !!this.$store.session?.canEditFixture?.();
  },

  fixtureEditHref(fixtureId) {
    const id = String(fixtureId || '').trim();
    if (!id) return '#';
    return withLeagueId(`/admin/fixtures/${id}/edit`, this.fetchParams.league);
  },

  // ---------- Navigation & formatting ----------
  goFixture(fx) {
    if (fx.type === 'event' && fx.fixtureId) {
      globalThis.location.href = withLeagueId(`/admin/fixtures/${fx.fixtureId}`, this.fetchParams.league);
    }
  },
  formatDate(iso) { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); },
  formatWeekday(iso) { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' }); },
  mapFor(venue) { return this.mapLinks?.[venue] || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`; },
}));
